import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Button from 'react-bootstrap/Button'
import Dropdown from 'react-bootstrap/Dropdown'
import Spinner from 'react-bootstrap/Spinner'
import ToggleButton from 'react-bootstrap/ToggleButton'
import ToggleButtonGroup from 'react-bootstrap/ToggleButtonGroup'
import {
  getCountries,
  getActiveUpdate,
  getLatestSuccessfulTeamUpdate,
  getBotSeries,
  getFavoriteTeamIds,
  getAverageTeams,
  getMyCup,
  searchMyCupTeams,
  getSession,
  getTeams,
  getFilteredTeams,
  runCountrySync,
  runBotCleanupSync,
  runFlagSync,
  runMatchSync,
  runOldPlayerDetailsSync,
  runPlayerSync,
  runSync,
  startLogin,
  saveUserSavedFilters,
  toggleFavoriteTeam,
} from './api'
import TeamsTable from './components/TeamsTable'
import BotSeriesTable from './components/BotSeriesTable'
import { sortTeamRows } from './utils/teamSorters'
import { cacheTeamsForQuery, selectTeamsCacheByKey } from './store/teamsCacheSlice'
import {
  BASE_COLUMNS,
  MY_CUP_COLUMNS,
  TEAM_MODE_COLUMNS,
  PLAYERS_MODE_COLUMNS,
  DISCORD_NO_VALUE,
  FAVORITE_VALUE,
  MY_CUP_VALUE,
  AVERAGE_TEAM_VALUE,
  BOT_SERIES_VALUE,
  BOT_SERIES_LEVEL_PREFIX,
  MAX_YOUTH_VALUE,
  PLAYER_STATS_SCOPE_ALL,
  PLAYER_STATS_SCOPE_TOP11,
  SAVED_FILTER_VALUE_PREFIX,
  SERIES_VALUE_PREFIX,
  SPECIALTY_MINUS_VALUE,
  SPECIALTY_PLUS_VALUE,
  TABLE_MODE_PLAYERS,
  TABLE_MODE_TEAM,
} from './app/constants'
import {
  buildTeamsCacheKey,
  getStoredOAuthToken,
  readPathToken,
  readMyCupTeamId,
  resolveInitialSelectionFromPathToken,
  resolveSelectionFromPathToken,
  selectAddLiveMatch,
  toPathTokenFromSelection,
} from './app/urlHelpers'
import { getColumnClassName, renderColumnLabel } from './app/columnHelpers'
import { mapTeamRow } from './app/teamRow'

const COUNTRY_SCOPE_USER = 'user'
const COUNTRY_SCOPE_TEAM = 'team'
const SERIES_LABEL_PATTERN = /^([A-Za-z0-9]+)\.(\d+)$/
const FILTER_SELECTION_ALL_KEY = 'all-countries'
const FILTER_COUNTRY_VALUE = 'FILTER'
const MULTI_SELECT_FILTER_KEYS = ['userCountryId', 'teamCountryId', 'cup', 'coachType', 'seriesLevel']
const SCHEDULE_TIME_ZONE = 'Europe/Helsinki'
const AUTOMATIC_UPDATE_SCHEDULES = [
  { label: 'Teams, countries, cup', weekday: 4, hour: 12, minute: 0 },
  { label: 'Matches, league details, cup', weekday: 6, hour: 22, minute: 0 },
  { label: 'Matches, cup', weekday: 2, hour: 22, minute: 30 },
]
const SPECIAL_THEME_CLASS_MAP = {
  premium: {
    loginName: 'premiumLoginName',
    selected: 'discordPremiumSelected',
    option: 'discordPremiumOption',
    ownRow: 'ownTeamRowPremium',
  },
  homoboy: {
    loginName: 'homoboyLoginName',
    selected: 'discordHomoboySelected',
    option: 'discordHomoboyOption',
    ownRow: 'ownTeamRowHomoboy',
  },
  retsnam: {
    loginName: 'retsnamLoginName',
    selected: 'discordRetsnamSelected',
    option: 'discordRetsnamOption',
    ownRow: 'ownTeamRowRetsnam',
  },
}

const FILTER_DEFAULTS = {
  userCountryId: [],
  userCountrySearch: '',
  teamCountryId: [],
  teamCountrySearch: '',
  cup: [],
  coachSkillMin: '',
  coachSkillMax: '',
  coachLeadershipMin: '',
  coachLeadershipMax: '',
  coachType: [],
  youth: '',
  fansMin: '',
  fansMax: '',
  arenaMin: '',
  arenaMax: '',
  hatstatsMin: '',
  hatstatsMax: '',
  ptsMin: '',
  ptsMax: '',
  posMin: '',
  posMax: '',
  seriesLevel: [],
  filterLimit: '4096',
  discord: '',
}

const CUP_FILTER_OPTIONS = [
  { value: '', label: 'Any' },
  { value: 'A', label: 'Cup A' },
  { value: 'B1', label: 'Cup B1' },
  { value: 'B2', label: 'Cup B2' },
  { value: 'B3', label: 'Cup B3' },
  { value: 'C', label: 'Cup C' },
  { value: 'NONE', label: 'No cup' },
]

const SERIES_FILTER_OPTIONS = [
  { value: '', label: 'Any' },
  { value: '1', label: 'I' },
  { value: '2', label: 'II' },
  { value: '3', label: 'III' },
  { value: '4', label: 'IV' },
  { value: '5', label: 'V' },
  { value: '6', label: 'VI' },
  { value: '7', label: 'VII' },
]

const hasFilterValues = filters => {
  if (!filters) {
    return false
  }

  return Object.entries(filters).some(([key, value]) => {
    if (key === 'filterLimit') {
      return `${value ?? ''}`.trim() !== '' && `${value ?? ''}` !== `${FILTER_DEFAULTS.filterLimit}`
    }

    if (Array.isArray(value)) {
      return value.some(item => `${item ?? ''}`.trim() !== '')
    }

    return `${value ?? ''}`.trim() !== ''
  })
}

const normalizeMultiFilterValues = value => {
  const values = Array.isArray(value) ? value : `${value ?? ''}`.split(',')

  return values
    .map(item => `${item ?? ''}`.trim())
    .filter(item => item !== '')
}

const normalizeFilterValues = filters => {
  return Object.entries(FILTER_DEFAULTS).reduce((accumulator, [key, defaultValue]) => {
    const value = filters?.[key]

    if (MULTI_SELECT_FILTER_KEYS.includes(key)) {
      accumulator[key] = normalizeMultiFilterValues(value)
      return accumulator
    }

    accumulator[key] = value === undefined || value === null ? defaultValue : `${value}`
    return accumulator
  }, {})
}

const normalizeSavedFilters = savedFilters => {
  if (!Array.isArray(savedFilters)) {
    return []
  }

  return savedFilters
    .map(item => ({
      id: `${item?.id || ''}`.trim(),
      name: `${item?.name || ''}`.trim(),
      filters: normalizeFilterValues(item?.filters),
    }))
    .filter(item => item.id && item.name)
}

const createSavedFilterId = () => {
  return `${Date.now()}-${Math.random().toString(16).slice(2, 8)}`
}

const getFilterSelectionKey = selectedCountryCode => {
  if (`${selectedCountryCode || ''}`.startsWith(SAVED_FILTER_VALUE_PREFIX)) {
    return `${selectedCountryCode}`
  }

  if (`${selectedCountryCode || ''}` === '') {
    return FILTER_SELECTION_ALL_KEY
  }

  if (`${selectedCountryCode || ''}` === FILTER_COUNTRY_VALUE) {
    return FILTER_COUNTRY_VALUE
  }

  return ''
}

const parseSeriesLabel = value => {
  const normalized = `${value || ''}`.trim().toUpperCase()
  const prefixedMatch = normalized.match(/^SERIES-(\d+)$/)

  if (prefixedMatch) {
    const number = Number.parseInt(prefixedMatch[1], 10)
    if (!Number.isInteger(number) || number <= 0) {
      return null
    }

    return {
      prefix: null,
      number,
      token: `SERIES-${number}`,
    }
  }

  const match = normalized.match(SERIES_LABEL_PATTERN)

  if (!match) {
    return null
  }

  const number = Number.parseInt(match[2], 10)
  if (!Number.isInteger(number) || number <= 0) {
    return null
  }

  return {
    prefix: match[1],
    number,
    token: `${match[1]}.${number}`,
  }
}

const parseSeriesPrefix = value => {
  const normalized = `${value || ''}`.trim().toUpperCase()

  if (!normalized || normalized.startsWith('SERIES-')) {
    return null
  }

  const strictMatch = normalized.match(/^([A-Z0-9]+)$/)
  if (strictMatch) {
    return strictMatch[1]
  }

  const leadingMatch = normalized.match(/^([A-Z0-9]+)\b/)
  if (leadingMatch) {
    return leadingMatch[1]
  }

  return null
}

const getTimeZoneParts = (date, timeZone) => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date)

  return parts.reduce((result, part) => {
    if (part.type !== 'literal') {
      result[part.type] = Number(part.value)
    }
    return result
  }, {})
}

const zonedDateTimeToDate = ({ year, month, day, hour, minute, timeZone }) => {
  const targetMillis = Date.UTC(year, month - 1, day, hour, minute)
  let utcMillis = targetMillis

  for (let attempt = 0; attempt < 3; attempt += 1) {
    const resolved = getTimeZoneParts(new Date(utcMillis), timeZone)
    const resolvedMillis = Date.UTC(
      resolved.year,
      resolved.month - 1,
      resolved.day,
      resolved.hour,
      resolved.minute
    )
    const adjustment = targetMillis - resolvedMillis

    if (adjustment === 0) {
      break
    }

    utcMillis += adjustment
  }

  return new Date(utcMillis)
}

const getNextScheduledUpdate = ({ weekday, hour, minute }) => {
  const now = new Date()
  const helsinkiNow = getTimeZoneParts(now, SCHEDULE_TIME_ZONE)
  const helsinkiCalendarDate = new Date(Date.UTC(
    helsinkiNow.year,
    helsinkiNow.month - 1,
    helsinkiNow.day
  ))
  let daysUntil = (weekday - helsinkiCalendarDate.getUTCDay() + 7) % 7
  let targetDate = new Date(helsinkiCalendarDate)
  targetDate.setUTCDate(targetDate.getUTCDate() + daysUntil)
  let scheduledAt = zonedDateTimeToDate({
    year: targetDate.getUTCFullYear(),
    month: targetDate.getUTCMonth() + 1,
    day: targetDate.getUTCDate(),
    hour,
    minute,
    timeZone: SCHEDULE_TIME_ZONE,
  })

  if (scheduledAt <= now) {
    daysUntil += 7
    targetDate = new Date(helsinkiCalendarDate)
    targetDate.setUTCDate(targetDate.getUTCDate() + daysUntil)
    scheduledAt = zonedDateTimeToDate({
      year: targetDate.getUTCFullYear(),
      month: targetDate.getUTCMonth() + 1,
      day: targetDate.getUTCDate(),
      hour,
      minute,
      timeZone: SCHEDULE_TIME_ZONE,
    })
  }

  return scheduledAt
}

const App = () => {
  const dispatch = useDispatch()
  const teamsCacheByKey = useSelector(selectTeamsCacheByKey)
  const initialPathToken = readPathToken()
  const initialMyCupTeamId = readMyCupTeamId()
  const [oauthToken, setOauthToken] = useState(getStoredOAuthToken)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [currentUser, setCurrentUser] = useState(null)
  const [canSync, setCanSync] = useState(false)
  const [teams, setTeams] = useState([])
  const [cupMatches, setCupMatches] = useState([])
  const [cupPairs, setCupPairs] = useState(null)
  const [cupPairTeams, setCupPairTeams] = useState({ current: [], next: [] })
  const [botSeriesRows, setBotSeriesRows] = useState([])
  const [favoriteTeamIds, setFavoriteTeamIds] = useState([])
  const [countries, setCountries] = useState([])
  const [lastSuccessfulTeamUpdate, setLastSuccessfulTeamUpdate] = useState(null)
  const [isUpdateScheduleOpen, setIsUpdateScheduleOpen] = useState(false)
  const [activeUpdate, setActiveUpdate] = useState(null)
  const [pathToken, setPathToken] = useState(initialPathToken)
  const [myCupTeamId, setMyCupTeamId] = useState(initialMyCupTeamId)
  const [cupTeam, setCupTeam] = useState(null)
  const [myCupSearchTerm, setMyCupSearchTerm] = useState('')
  const [myCupSearchResults, setMyCupSearchResults] = useState([])
  const [isMyCupSearchLoading, setIsMyCupSearchLoading] = useState(false)
  const [seriesJumpInput, setSeriesJumpInput] = useState('')
  const [isTeamsLoading, setIsTeamsLoading] = useState(true)
  const [isBotSeriesLoading, setIsBotSeriesLoading] = useState(true)
  const [isCountriesLoading, setIsCountriesLoading] = useState(true)
  const [selectedCountryCode, setSelectedCountryCode] = useState(
    resolveInitialSelectionFromPathToken(initialPathToken)
  )
  const [countryScope, setCountryScope] = useState(COUNTRY_SCOPE_USER)
  const [tableMode, setTableMode] = useState(TABLE_MODE_TEAM)
  const [playerStatsScope, setPlayerStatsScope] = useState(PLAYER_STATS_SCOPE_ALL)
  const [sortConfig, setSortConfig] = useState({ key: 'hatstats', direction: 'desc' })
  const [coachTypeTop, setCoachTypeTop] = useState('D')
  const [status, setStatus] = useState(() => (
    getStoredOAuthToken()
      ? 'Checking session...'
      : 'By login in you can add favourites and track your league teams. Without login you see only the top50 teams.'
  ))
  const [syncing, setSyncing] = useState(false)
  const [flagSyncing, setFlagSyncing] = useState(false)
  const [playerSyncing, setPlayerSyncing] = useState(false)
  const [oldPlayerDetailsSyncing, setOldPlayerDetailsSyncing] = useState(false)
  const [matchSyncing, setMatchSyncing] = useState(false)
  const [botCleanupSyncing, setBotCleanupSyncing] = useState(false)
  const [countrySyncing, setCountrySyncing] = useState(false)
  const [databaseSearchDirections, setDatabaseSearchDirections] = useState({})
  const [isFilterOpen, setIsFilterOpen] = useState(false)
  const [isAdminOpen, setIsAdminOpen] = useState(false)
  const [filterDraft, setFilterDraft] = useState(FILTER_DEFAULTS)
  const [appliedFilters, setAppliedFilters] = useState(FILTER_DEFAULTS)
  const [savedFilters, setSavedFilters] = useState([])
  const [filterStatesBySelection, setFilterStatesBySelection] = useState({})
  const [filterNameDraft, setFilterNameDraft] = useState('')
  const [sessionReady, setSessionReady] = useState(() => !getStoredOAuthToken())
  const fetchTeamsRequestRef = useRef(0)
  const sortConfigRef = useRef(sortConfig)
  const playerStatsScopeRef = useRef(playerStatsScope)
  const loadedSavedFilterIdRef = useRef('')
  const lastFilterSelectionKeyRef = useRef('')
  const filterStatesBySelectionRef = useRef(filterStatesBySelection)
  const statusTimerRef = useRef(null)

  const decodeHtmlEntities = value => {
    return `${value || ''}`
      .replace(/&amp;/g, '&')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
  }

  useEffect(() => {
    filterStatesBySelectionRef.current = filterStatesBySelection
  }, [filterStatesBySelection])

  useEffect(() => {
    if (statusTimerRef.current) {
      clearTimeout(statusTimerRef.current)
      statusTimerRef.current = null
    }

    if (!status) {
      return undefined
    }

    statusTimerRef.current = setTimeout(() => {
      setStatus('')
      statusTimerRef.current = null
    }, 5000)

    return () => {
      if (statusTimerRef.current) {
        clearTimeout(statusTimerRef.current)
        statusTimerRef.current = null
      }
    }
  }, [status])

  const normalizeCountryName = countryName => {
    const decodedName = decodeHtmlEntities(countryName)
    const normalized = `${decodedName || ''}`.trim().toLowerCase()

    if (normalized === 'cymru') {
      return 'Wales'
    }

    return decodedName
  }

  useEffect(() => {
    const handlePopState = () => {
      setPathToken(readPathToken())
      setMyCupTeamId(readMyCupTeamId())
    }

    window.addEventListener('popstate', handlePopState)
    return () => {
      window.removeEventListener('popstate', handlePopState)
    }
  }, [])

  useEffect(() => {
    const term = myCupSearchTerm.trim()
    if (term.length < 4) {
      setMyCupSearchResults([])
      setIsMyCupSearchLoading(false)
      return undefined
    }

    let isCurrent = true
    const timer = setTimeout(async () => {
      setIsMyCupSearchLoading(true)
      try {
        const results = await searchMyCupTeams(term)
        if (isCurrent) {
          setMyCupSearchResults(Array.isArray(results) ? results : [])
        }
      } catch {
        if (isCurrent) {
          setMyCupSearchResults([])
        }
      } finally {
        if (isCurrent) {
          setIsMyCupSearchLoading(false)
        }
      }
    }, 250)

    return () => {
      isCurrent = false
      clearTimeout(timer)
    }
  }, [myCupSearchTerm])

  const teamRows = useMemo(() => {
    const mappedTeams = teams.map(team => mapTeamRow(team, playerStatsScope))
    const effectiveSortConfig =
      selectedCountryCode === AVERAGE_TEAM_VALUE && sortConfig.key === 'hatstats'
        ? { key: 'series', direction: 'asc' }
        : sortConfig

    return sortTeamRows({
      teams: mappedTeams,
      sortBy: effectiveSortConfig.key,
      direction: effectiveSortConfig.direction,
      coachTypeTop,
    })
  }, [teams, sortConfig, playerStatsScope, coachTypeTop, selectedCountryCode])

  const cupCurrentTeamRows = useMemo(
    () => cupPairTeams.current.map(team => mapTeamRow(team, playerStatsScope)),
    [cupPairTeams, playerStatsScope]
  )
  const cupNextTeamRows = useMemo(
    () => cupPairTeams.next.map(team => mapTeamRow(team, playerStatsScope)),
    [cupPairTeams, playerStatsScope]
  )
  const cupNextMatchPredictions = useMemo(() => {
    const ownTeamIds = [cupTeam?.teamId, currentUser?.teamId, currentUser?.TeamID]
      .map(value => Number(value))
      .filter(Number.isInteger)
    const ownUserIds = [currentUser?.userId, currentUser?.UserID]
      .map(value => Number(value))
      .filter(Number.isInteger)
    const isOwnCupTeam = team => ownTeamIds.includes(Number(team?.teamId ?? team?.TeamID))
      || ownUserIds.includes(Number(team?.UserID))
    const ownPairTeam = cupPairs?.current?.teams?.find(isOwnCupTeam)
    const ownCupTeam = cupPairTeams.current.find(isOwnCupTeam)
    const ownTeam = cupCurrentTeamRows.find(isOwnCupTeam)
    const teamName = cupTeam?.teamName
      || ownPairTeam?.teamName
      || ownCupTeam?.FemmeTeam?.TeamName
      || ownCupTeam?.PrimaryTeam?.TeamName
      || ownTeam?.teamName

    if (!teamName) {
      return []
    }

    return (cupPairs?.next?.teams || []).map(opponent => {
      const opponentName = opponent.teamName || 'Unknown opponent'
      return opponent.ownNextVenue === 'home'
        ? { homeTeamName: teamName, awayTeamName: opponentName }
        : opponent.ownNextVenue === 'away'
          ? { homeTeamName: opponentName, awayTeamName: teamName }
          : { homeTeamName: teamName, awayTeamName: opponentName }
    })
  }, [
    cupCurrentTeamRows,
    cupPairTeams,
    cupPairs,
    cupTeam,
    currentUser?.TeamID,
    currentUser?.UserID,
    currentUser?.teamId,
    currentUser?.userId,
  ])

  useEffect(() => {
    sortConfigRef.current = sortConfig
  }, [sortConfig])

  useEffect(() => {
    playerStatsScopeRef.current = playerStatsScope
  }, [playerStatsScope])

  useEffect(() => {
    if (!sessionReady) {
      return
    }

    if (!currentUser) {
      setSavedFilters([])
      setFilterStatesBySelection({})
      loadedSavedFilterIdRef.current = ''
      setFilterNameDraft('')
      return
    }

    setSavedFilters(normalizeSavedFilters(currentUser.savedFilters))
    loadedSavedFilterIdRef.current = ''
    setFilterNameDraft('')
  }, [currentUser, sessionReady])

  const visibleColumns = useMemo(() => {
    const modeColumns = tableMode === TABLE_MODE_TEAM ? TEAM_MODE_COLUMNS : PLAYERS_MODE_COLUMNS
    const cupColumns = selectedCountryCode === MY_CUP_VALUE ? MY_CUP_COLUMNS : []
    const nonDuplicateModeColumns = modeColumns.filter(
      modeColumn => !cupColumns.some(cupColumn => cupColumn.key === modeColumn.key)
    )
    return [...BASE_COLUMNS, ...cupColumns, ...nonDuplicateModeColumns]
  }, [selectedCountryCode, tableMode])

  const cupMatchColumns = useMemo(
    () => visibleColumns.filter(column => column.key !== 'youth' && column.key !== 'fanClub'),
    [visibleColumns]
  )

  const countriesForSelect = useMemo(() => {
    const userCountryId = Number.parseInt(currentUser?.countryId, 10)

    if (Number.isNaN(userCountryId)) {
      return countries
    }

    const ownCountry = countries.find(country => Number(country?.CountryID) === userCountryId)
    if (!ownCountry) {
      return countries
    }

    return [ownCountry, ...countries.filter(country => Number(country?.CountryID) !== userCountryId)]
  }, [countries, currentUser])

  const selectedSavedFilter = useMemo(() => {
    if (!selectedCountryCode.startsWith(SAVED_FILTER_VALUE_PREFIX)) {
      return null
    }

    const savedFilterId = selectedCountryCode.slice(SAVED_FILTER_VALUE_PREFIX.length)
    return savedFilters.find(filter => filter.id === savedFilterId) || null
  }, [savedFilters, selectedCountryCode])

  const isSavedFilterSelection = Boolean(selectedSavedFilter)
  const isFilterCountrySelection = selectedCountryCode === FILTER_COUNTRY_VALUE
  const isBotSeriesSelection =
    selectedCountryCode === BOT_SERIES_VALUE || selectedCountryCode.startsWith(BOT_SERIES_LEVEL_PREFIX)
  const activeFilterSelectionKey = getFilterSelectionKey(selectedCountryCode)

  useEffect(() => {
    if (!sessionReady) {
      return
    }

    if (!selectedCountryCode.startsWith(SAVED_FILTER_VALUE_PREFIX)) {
      return
    }

    if (selectedSavedFilter) {
      return
    }

    setSelectedCountryCode('')
    setPathToken('')
    loadedSavedFilterIdRef.current = ''
  }, [selectedCountryCode, selectedSavedFilter, sessionReady])

  const getMultiSelectValues = key => {
    const value = filterDraft[key]
    return Array.isArray(value) ? value : []
  }

  const toggleMultiSelectValue = (key, value) => {
    const normalizedValue = `${value || ''}`.trim()

    if (!normalizedValue) {
      return
    }

    setFilterDraft(current => {
      const currentValues = Array.isArray(current[key]) ? current[key] : []
      const nextValues = currentValues.includes(normalizedValue)
        ? currentValues.filter(item => item !== normalizedValue)
        : [...currentValues, normalizedValue]

      return {
        ...current,
        [key]: nextValues,
      }
    })
  }

  const clearMultiSelectValues = key => {
    setFilterDraft(current => ({
      ...current,
      [key]: [],
    }))
  }

  const hasSavedFilterNameConflict = (name, excludeId = '') => {
    const normalizedName = `${name || ''}`.trim().toLowerCase()

    if (!normalizedName) {
      return false
    }

    return savedFilters.some(filter => {
      if (excludeId && filter.id === excludeId) {
        return false
      }

      return `${filter.name || ''}`.trim().toLowerCase() === normalizedName
    })
  }

  const getMultiSelectSummary = (values, resolveLabel) => {
    if (!values.length) {
      return 'Any'
    }

    if (values.length === 1) {
      return resolveLabel(values[0])
    }

    return `${values.length} selected`
  }

  useEffect(() => {
    if (!activeFilterSelectionKey) {
      return
    }

    const storedState = filterStatesBySelectionRef.current[activeFilterSelectionKey]
    const baseDraft = selectedSavedFilter ? normalizeFilterValues(selectedSavedFilter.filters) : FILTER_DEFAULTS
    const baseName = selectedSavedFilter ? selectedSavedFilter.name : ''
    const expectedSavedFilterId = selectedSavedFilter ? selectedSavedFilter.id : ''
    const hasMatchingStored = Boolean(storedState && storedState.savedFilterId === expectedSavedFilterId)
    const nextDraft = normalizeFilterValues(hasMatchingStored ? storedState?.draft : baseDraft)
    const nextAppliedFilters = normalizeFilterValues(hasMatchingStored ? storedState?.appliedFilters : baseDraft)
    const nextNameDraft = `${hasMatchingStored ? storedState?.nameDraft : baseName}`.trim()

    setFilterDraft(nextDraft)
    setAppliedFilters(nextAppliedFilters)
    setFilterNameDraft(nextNameDraft)

    if (selectedSavedFilter) {
      loadedSavedFilterIdRef.current = selectedSavedFilter.id
    } else {
      loadedSavedFilterIdRef.current = ''
    }
  }, [activeFilterSelectionKey, selectedSavedFilter])

  useEffect(() => {
    if (!activeFilterSelectionKey) {
      return
    }

    if (selectedCountryCode.startsWith(SAVED_FILTER_VALUE_PREFIX) && !selectedSavedFilter) {
      return
    }

    if (lastFilterSelectionKeyRef.current !== activeFilterSelectionKey) {
      lastFilterSelectionKeyRef.current = activeFilterSelectionKey
      return
    }

    setFilterStatesBySelection(current => ({
      ...current,
      [activeFilterSelectionKey]: {
        draft: filterDraft,
        appliedFilters,
        nameDraft: filterNameDraft,
        savedFilterId: selectedSavedFilter ? selectedSavedFilter.id : '',
      },
    }))
  }, [activeFilterSelectionKey, appliedFilters, filterDraft, filterNameDraft, selectedCountryCode, selectedSavedFilter])

  const getCountryLabel = country => {
    return normalizeCountryName(country?.CountryName) || country?.CountryCode || country?.CountryID || '-'
  }

  const getCountryLabelById = countryId => {
    const normalizedId = `${countryId || ''}`
    if (!normalizedId) {
      return 'Any'
    }

    const found = countriesForSelect.find(country => `${country?.CountryID || ''}` === normalizedId)
    return found ? getCountryLabel(found) : 'Any'
  }

  const selectedFilterChips = []

  getMultiSelectValues('userCountryId').forEach(value => {
    selectedFilterChips.push({
      key: `userCountryId-${value}`,
      label: `User country: ${getCountryLabelById(value)}`,
      onRemove: () => toggleMultiSelectValue('userCountryId', value),
    })
  })

  getMultiSelectValues('teamCountryId').forEach(value => {
    selectedFilterChips.push({
      key: `teamCountryId-${value}`,
      label: `Team country: ${getCountryLabelById(value)}`,
      onRemove: () => toggleMultiSelectValue('teamCountryId', value),
    })
  })

  getMultiSelectValues('cup').forEach(value => {
    const optionLabel = CUP_FILTER_OPTIONS.find(option => option.value === value)?.label || value
    selectedFilterChips.push({
      key: `cup-${value}`,
      label: `Cup: ${optionLabel}`,
      onRemove: () => toggleMultiSelectValue('cup', value),
    })
  })

  getMultiSelectValues('coachType').forEach(value => {
    const optionLabel =
      value === 'D' ? 'Defensive' : value === 'O' ? 'Offensive' : value === 'N' ? 'Neutral' : value
    selectedFilterChips.push({
      key: `coachType-${value}`,
      label: `Coach type: ${optionLabel}`,
      onRemove: () => toggleMultiSelectValue('coachType', value),
    })
  })

  getMultiSelectValues('seriesLevel').forEach(value => {
    const optionLabel = SERIES_FILTER_OPTIONS.find(option => option.value === value)?.label || value
    selectedFilterChips.push({
      key: `seriesLevel-${value}`,
      label: `Series: ${optionLabel}`,
      onRemove: () => toggleMultiSelectValue('seriesLevel', value),
    })
  })

  const hasActiveFilters = useMemo(() => hasFilterValues(appliedFilters), [appliedFilters])

  const hasDraftFilters = useMemo(() => hasFilterValues(filterDraft), [filterDraft])

  const filtersDirty = useMemo(
    () => JSON.stringify(filterDraft) !== JSON.stringify(appliedFilters),
    [filterDraft, appliedFilters]
  )

  const countryExtraOptions = useMemo(() => {
    const options = []
    const userCountryId = Number.parseInt(currentUser?.countryId, 10)

    if (isAuthenticated && userCountryId === 11) {
      options.push({
        value: DISCORD_NO_VALUE,
        label: 'DISCORD NO!',
      })
    }

    const seriesId = Number.parseInt(currentUser?.femmeSeries?.seriesId, 10)
    if (isAuthenticated && Number.isInteger(seriesId) && seriesId > 0) {
      const seriesName = currentUser?.femmeSeries?.seriesName || `Series ${seriesId}`
      const seriesNameParsed = parseSeriesLabel(seriesName)
      const seriesSelectionNumber = seriesNameParsed?.number || seriesId

      options.push({
        value: `${SERIES_VALUE_PREFIX}${seriesSelectionNumber}`,
        label: `My series: ${seriesName}`,
      })
    }

    options.push({
      value: `${BOT_SERIES_LEVEL_PREFIX}VI`,
      label: 'Bot series VI',
    })

    options.push({
      value: `${BOT_SERIES_LEVEL_PREFIX}VII`,
      label: 'Bot series VII',
    })

    return options
  }, [currentUser, isAuthenticated])

  const savedFilterOptions = useMemo(() => {
    return savedFilters.map(savedFilter => ({
      value: `${SAVED_FILTER_VALUE_PREFIX}${savedFilter.id}`,
      label: `Filter: ${savedFilter.name}`,
    }))
  }, [savedFilters])

  const specialTheme = `${currentUser?.special?.theme || ''}`
  const specialThemeClasses = SPECIAL_THEME_CLASS_MAP[specialTheme] || null
  const favoriteOptionLabel = currentUser?.special?.favoriteLabel || 'Favorite'
  const favoriteSelectClassName =
    specialThemeClasses && selectedCountryCode === FAVORITE_VALUE
      ? specialThemeClasses.selected
      : undefined

  const selectedSeriesId = useMemo(() => {
    if (!selectedCountryCode.startsWith(SERIES_VALUE_PREFIX)) {
      return null
    }

    const parsed = Number.parseInt(selectedCountryCode.slice(SERIES_VALUE_PREFIX.length), 10)
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null
  }, [selectedCountryCode])

  const activeSeries = useMemo(() => {
    if (!selectedSeriesId) {
      return null
    }

    const candidates = [
      pathToken,
      currentUser?.femmeSeries?.seriesName,
      teams[0]?.LeagueDetails?.LeagueLevelUnitName,
      teams[0]?.FemmeTeam?.LeagueLevelUnit?.LeagueLevelUnitName,
    ]

    let fallbackSeries = null

    for (const candidate of candidates) {
      const parsed = parseSeriesLabel(candidate)
      if (parsed && parsed.number === selectedSeriesId) {
        if (parsed.prefix) {
          return parsed
        }

        if (!fallbackSeries) {
          fallbackSeries = parsed
        }
      }
    }

    const prefixCandidates = [
      currentUser?.femmeSeries?.seriesName,
      teams[0]?.LeagueDetails?.LeagueLevelUnitName,
      teams[0]?.FemmeTeam?.LeagueLevelUnit?.LeagueLevelUnitName,
      teamRows[0]?.series,
    ]

    for (const candidate of prefixCandidates) {
      const prefix = parseSeriesPrefix(candidate)

      if (prefix) {
        return {
          prefix,
          number: selectedSeriesId,
          token: `${prefix}.${selectedSeriesId}`,
        }
      }
    }

    return fallbackSeries
  }, [currentUser, pathToken, selectedSeriesId, teams, teamRows])

  useEffect(() => {
    if (activeSeries) {
      setSeriesJumpInput(activeSeries.token)
      return
    }

    if (selectedSeriesId) {
      setSeriesJumpInput(`${selectedSeriesId}`)
      return
    }

    setSeriesJumpInput('')
  }, [activeSeries, selectedSeriesId])

  const userTimeZone = useMemo(() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
    } catch (error) {
      return 'UTC'
    }
  }, [])

  const automaticUpdateSchedule = useMemo(() => {
    const formatter = new Intl.DateTimeFormat(undefined, {
      weekday: 'long',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: userTimeZone,
    })

    return AUTOMATIC_UPDATE_SCHEDULES.map(schedule => ({
      label: schedule.label,
      schedule: formatter.format(getNextScheduledUpdate(schedule)),
    }))
  }, [userTimeZone])

  const refreshLastSuccessfulTeamUpdate = useCallback(async () => {
    try {
      const result = await getLatestSuccessfulTeamUpdate()
      setLastSuccessfulTeamUpdate(result?.lastSuccessAt || null)
    } catch (error) {
      // The displayed timestamp can remain stale if the update log is temporarily unavailable.
    }
  }, [])

  useEffect(() => {
    refreshLastSuccessfulTeamUpdate()
  }, [refreshLastSuccessfulTeamUpdate])

  useEffect(() => {
    let isCurrent = true

    const refreshActiveUpdate = async () => {
      try {
        const result = await getActiveUpdate()
        if (isCurrent) {
          setActiveUpdate(result?.update || null)
        }
      } catch (error) {
        // Keep the last known state while the status endpoint is briefly unavailable.
      }
    }

    refreshActiveUpdate()
    const intervalId = window.setInterval(refreshActiveUpdate, 1500)

    return () => {
      isCurrent = false
      window.clearInterval(intervalId)
    }
  }, [])

  const isUpdateInProgress = syncing || Boolean(activeUpdate)
  const updateProgressLabel = activeUpdate?.label || 'Updating teams'
  const updateProgressPercent = activeUpdate?.total
    ? Math.min(100, Math.round((activeUpdate.completed / activeUpdate.total) * 100))
    : null

  const firstTeamLastUpdatedLabel = useMemo(() => {
    const rawLastUpdated = lastSuccessfulTeamUpdate

    if (!rawLastUpdated) {
      return null
    }

    const parsed = new Date(rawLastUpdated)

    if (Number.isNaN(parsed.getTime())) {
      return `${rawLastUpdated}`
    }

    return parsed.toLocaleString(undefined, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: userTimeZone,
      timeZoneName: 'short',
    })
  }, [lastSuccessfulTeamUpdate, userTimeZone])

  const toggleSort = sortKey => {
    if (sortKey === 'coachTypeOrder') {
      setCoachTypeTop(current => (current === 'D' ? 'O' : current === 'O' ? 'N' : 'D'))
      setSortConfig({ key: 'coachTypeOrder', direction: 'desc' })
      return
    }

    const shouldDefaultToDesc =
      sortKey === 'youthTopSalary' ||
      sortKey === 'points' ||
      sortKey === 'youthTeamMode' ||
      sortKey === 'coachTypeOrder'

    setSortConfig(current => {
      if (current.key === sortKey) {
        return {
          key: sortKey,
          direction: current.direction === 'asc' ? 'desc' : 'asc',
        }
      }

      return {
        key: sortKey,
        direction: shouldDefaultToDesc ? 'desc' : 'asc',
      }
    })
  }

  const handleCountrySelectionChange = value => {
    setMyCupTeamId(null)
    setSelectedCountryCode(value)

    if (value === AVERAGE_TEAM_VALUE) {
      setSortConfig({ key: 'series', direction: 'asc' })
    }

    const nextToken = toPathTokenFromSelection({
      selectedCountryCode: value,
      countries,
      currentUser,
      pathToken,
    })
    setPathToken(nextToken)
  }

  const openMyCupTeam = teamId => {
    setMyCupTeamId(Number(teamId))
    setMyCupSearchTerm('')
    setMyCupSearchResults([])
    setSelectedCountryCode(MY_CUP_VALUE)
  }

  const jumpToSeries = useCallback(rawValue => {
    const normalized = `${rawValue || ''}`.trim().toUpperCase()
    const parsed = parseSeriesLabel(normalized)

    if (parsed) {
      setSelectedCountryCode(`${SERIES_VALUE_PREFIX}${parsed.number}`)
      setPathToken(parsed.token)
      return
    }

    const numberOnlyMatch = normalized.match(/^(\d+)$/)
    if (numberOnlyMatch) {
      const number = Number.parseInt(numberOnlyMatch[1], 10)

      if (Number.isInteger(number) && number > 0) {
        const activePrefix = activeSeries?.prefix
        setSelectedCountryCode(`${SERIES_VALUE_PREFIX}${number}`)
        setPathToken(activePrefix ? `${activePrefix}.${number}` : `SERIES-${number}`)
        return
      }
    }

    setStatus('Series format must be like VII.1443, SERIES-1443 or 1443')
  }, [activeSeries])

  const goToPreviousSeries = useCallback(() => {
    const currentSeriesNumber = activeSeries?.number ?? selectedSeriesId

    if (!Number.isInteger(currentSeriesNumber)) {
      return
    }

    const targetNumber = currentSeriesNumber - 1
    if (targetNumber < 1) {
      return
    }

    const prefixFromInput = parseSeriesLabel(seriesJumpInput)?.prefix
    const prefix = activeSeries?.prefix || prefixFromInput

    if (prefix) {
      jumpToSeries(`${prefix}.${targetNumber}`)
      return
    }

    jumpToSeries(`${targetNumber}`)
  }, [activeSeries, jumpToSeries, selectedSeriesId, seriesJumpInput])

  const goToNextSeries = useCallback(() => {
    const currentSeriesNumber = activeSeries?.number ?? selectedSeriesId

    if (!Number.isInteger(currentSeriesNumber)) {
      return
    }

    const targetNumber = currentSeriesNumber + 1

    const prefixFromInput = parseSeriesLabel(seriesJumpInput)?.prefix
    const prefix = activeSeries?.prefix || prefixFromInput

    if (prefix) {
      jumpToSeries(`${prefix}.${targetNumber}`)
      return
    }

    jumpToSeries(`${targetNumber}`)
  }, [activeSeries, jumpToSeries, selectedSeriesId, seriesJumpInput])

  const getSortArrow = sortKey => {
    if (sortConfig.key !== sortKey) {
      return ''
    }

    return sortConfig.direction === 'asc' ? '▲' : '▼'
  }

  const sortByCoachSkill = () => {
    setSortConfig({ key: 'coachSkill', direction: 'desc' })
  }

  const sortByCoachLeadership = () => {
    setSortConfig({ key: 'coachLeadership', direction: 'desc' })
  }

  const sampleTeams = ({ items, size }) => {
    const sampled = [...items]

    for (let index = sampled.length - 1; index > 0; index -= 1) {
      const randomIndex = Math.floor(Math.random() * (index + 1))
      const currentValue = sampled[index]
      sampled[index] = sampled[randomIndex]
      sampled[randomIndex] = currentValue
    }

    return sampled.slice(0, size)
  }

  const fetchTeams = useCallback(async (countryCode, options = {}) => {
    const requestId = fetchTeamsRequestRef.current + 1
    fetchTeamsRequestRef.current = requestId
    const isBotSeriesRequest = countryCode === BOT_SERIES_VALUE || countryCode.startsWith(BOT_SERIES_LEVEL_PREFIX)

    if (isBotSeriesRequest) {
      setIsBotSeriesLoading(true)
    } else {
      setIsTeamsLoading(true)
    }

    try {
      if (countryCode === BOT_SERIES_VALUE || countryCode.startsWith(BOT_SERIES_LEVEL_PREFIX)) {
        const level = countryCode.endsWith('VII') ? 'VII' : 'VI'
        const rows = await getBotSeries(level)

        if (requestId !== fetchTeamsRequestRef.current) {
          return
        }

        setBotSeriesRows(Array.isArray(rows) ? rows : [])
        return
      }

      if (countryCode === MY_CUP_VALUE) {
        const cup = await getMyCup({ oauthToken, teamId: myCupTeamId })

        if (requestId === fetchTeamsRequestRef.current) {
          setCupTeam(cup?.team || null)
          setTeams(Array.isArray(cup?.teams) ? cup.teams : [])
          setCupMatches(Array.isArray(cup?.cupMatches) ? cup.cupMatches : [])
          setCupPairs(cup?.cupPairs || null)
          setCupPairTeams({
            current: Array.isArray(cup?.currentMatchTeams) ? cup.currentMatchTeams : [],
            next: Array.isArray(cup?.possibleNextMatchTeams) ? cup.possibleNextMatchTeams : [],
          })
          setStatus(cup?.currentMatch ? 'Cup round 3: your opponent and the next possible opponents.' : 'No upcoming cup match was found for your team.')
        }
        return
      }

      if (countryCode === AVERAGE_TEAM_VALUE) {
        const averageTeams = await getAverageTeams({
          oauthToken,
          playerStatsScope: playerStatsScopeRef.current,
        })

        if (requestId === fetchTeamsRequestRef.current) {
          setTeams(Array.isArray(averageTeams) ? averageTeams : [])
          setStatus('Median team statistics by division level.')
        }
        return
      }

      const requestedSortConfig = options.sortConfig || sortConfigRef.current
      const requestedPlayerStatsScope = options.playerStatsScope || playerStatsScopeRef.current
      const isDiscordNoFilter = countryCode === DISCORD_NO_VALUE
      const isFavoriteFilter = countryCode === FAVORITE_VALUE
      const isMyCupFilter = countryCode === MY_CUP_VALUE
      const isMaxYouthFilter = countryCode === MAX_YOUTH_VALUE
      const isAllCountriesFilter = countryCode === ''
      const useFilteredSearch = isSavedFilterSelection || (isAllCountriesFilter && hasActiveFilters) || (isFilterCountrySelection && hasActiveFilters)
      const isSeriesFilter = countryCode.startsWith(SERIES_VALUE_PREFIX)
      const seriesId = isSeriesFilter
        ? Number.parseInt(countryCode.slice(SERIES_VALUE_PREFIX.length), 10)
        : undefined
      const specialtyMode =
        countryCode === SPECIALTY_PLUS_VALUE
          ? 'plus'
          : countryCode === SPECIALTY_MINUS_VALUE
            ? 'minus'
            : undefined
      const normalizedPathToken = `${pathToken || ''}`.trim()
      const seriesNameFromPathToken =
        isSeriesFilter && /^(?:[A-Za-z0-9]+)\.(\d+)$/.test(normalizedPathToken)
          ? normalizedPathToken
          : undefined

      const parsedCountryId = Number.parseInt(countryCode, 10)
      const filteredCountryId =
        specialtyMode || isDiscordNoFilter || isSeriesFilter || isFavoriteFilter || isMyCupFilter || isMaxYouthFilter
          ? undefined
          : Number.isInteger(parsedCountryId) && parsedCountryId > 0
            ? parsedCountryId
            : undefined

        // If we're on the main Filter page and no filters are set, don't load any teams.
        if (isFilterCountrySelection && !hasActiveFilters && !isSavedFilterSelection) {
          if (requestId === fetchTeamsRequestRef.current) {
            setTeams([])
            setIsTeamsLoading(false)
          }
          return
        }
      const limit =
        isAllCountriesFilter && !isDiscordNoFilter && !isSeriesFilter && !isFavoriteFilter && !isMyCupFilter && !isMaxYouthFilter
          ? 200
          : filteredCountryId || isDiscordNoFilter || isSeriesFilter || isFavoriteFilter || isMyCupFilter || isMaxYouthFilter
            ? undefined
            : 1000
      const cacheKey = buildTeamsCacheKey({
        favorite: isFavoriteFilter,
        filteredCountryId,
        specialtyMode,
        discordNo: isDiscordNoFilter,
        maxYouth: isMaxYouthFilter,
        seriesId: Number.isInteger(seriesId) ? seriesId : undefined,
        authScope: isAuthenticated ? 'auth' : 'guest',
        countryScope,
        useServerSort: isAllCountriesFilter && !specialtyMode && !isDiscordNoFilter && !isSeriesFilter && !isFavoriteFilter && !isMaxYouthFilter,
        sortBy: isAllCountriesFilter ? requestedSortConfig.key : undefined,
        sortDirection: isAllCountriesFilter ? requestedSortConfig.direction : undefined,
        playerStatsScope: isAllCountriesFilter ? requestedPlayerStatsScope : undefined,
      })
      const cachedEntry = isFavoriteFilter || useFilteredSearch ? null : teamsCacheByKey[cacheKey]

      if (cachedEntry?.items) {
        if (requestId === fetchTeamsRequestRef.current) {
          setTeams(cachedEntry.items)
          setIsTeamsLoading(false)
        }
        return
      }

      const items = useFilteredSearch
        ? await getFilteredTeams(appliedFilters)
        : await getTeams(filteredCountryId, limit, specialtyMode, {
            favorite: isFavoriteFilter,
            discordNo: isDiscordNoFilter,
            maxYouth: isMaxYouthFilter,
            seriesId: Number.isInteger(seriesId) ? seriesId : undefined,
            seriesName: seriesNameFromPathToken,
            oauthToken,
            countryScope,
            sortBy: isAllCountriesFilter ? requestedSortConfig.key : undefined,
            sortDirection: isAllCountriesFilter ? requestedSortConfig.direction : undefined,
            playerStatsScope: isAllCountriesFilter ? requestedPlayerStatsScope : undefined,
          })

      if (requestId !== fetchTeamsRequestRef.current) {
        return
      }

      if (!isFavoriteFilter && !useFilteredSearch) {
        dispatch(
          cacheTeamsForQuery({
            key: cacheKey,
            items,
          })
        )
      }
      setTeams(items)
    } finally {
      if (requestId === fetchTeamsRequestRef.current) {
        if (isBotSeriesRequest) {
          setIsBotSeriesLoading(false)
        } else {
          setIsTeamsLoading(false)
        }
      }
    }
  }, [appliedFilters, countryScope, dispatch, hasActiveFilters, isAuthenticated, isFilterCountrySelection, isSavedFilterSelection, myCupTeamId, oauthToken, pathToken, teamsCacheByKey])

  const runDatabaseSearch = useCallback(async sortKey => {
    if (!sortKey) {
      return
    }

    const defaultDirection =
      sortKey === 'leaguePosition' || sortKey === 'rank'
        ? 'asc'
        : 'desc'
    const currentDirection = databaseSearchDirections[sortKey] || defaultDirection
    const nextDirection = currentDirection === 'desc' ? 'asc' : 'desc'
    const nextSortConfig = {
      key: sortKey,
      direction: currentDirection,
    }
    setSortConfig(nextSortConfig)
    setDatabaseSearchDirections(currentDirections => ({
      ...currentDirections,
      [sortKey]: nextDirection,
    }))

    if (selectedCountryCode !== '') {
      return
    }

    await fetchTeams(selectedCountryCode, {
      sortConfig: nextSortConfig,
      playerStatsScope: playerStatsScopeRef.current,
    })
  }, [databaseSearchDirections, fetchTeams, selectedCountryCode])

  const fetchCountries = useCallback(async () => {
    setIsCountriesLoading(true)

    try {
      const items = await getCountries(countryScope)
      setCountries(items)
    } finally {
      setIsCountriesLoading(false)
    }
  }, [countryScope])

  useEffect(() => {
    fetchCountries()
  }, [fetchCountries])

  useEffect(() => {
    const loadFavorites = async () => {
      if (!oauthToken || !isAuthenticated) {
        setFavoriteTeamIds([])
        return
      }

      try {
        const data = await getFavoriteTeamIds(oauthToken)
        const ids = Array.isArray(data?.favoriteTeamIds)
          ? data.favoriteTeamIds.map(value => Number.parseInt(value, 10)).filter(Number.isInteger)
          : []
        setFavoriteTeamIds(ids)
      } catch (error) {
        setFavoriteTeamIds([])
      }
    }

    loadFavorites()
  }, [oauthToken, isAuthenticated])

  useEffect(() => {
    const resolvedSelection = resolveSelectionFromPathToken({
      pathToken,
      countries,
      currentUser,
    })

    if (resolvedSelection === null) {
      return
    }

    setSelectedCountryCode(current => {
      if (current === resolvedSelection) {
        return current
      }

      return resolvedSelection
    })
  }, [pathToken, countries, currentUser])

  useEffect(() => {
    if (!selectedCountryCode && pathToken) {
      return
    }

    let nextToken = toPathTokenFromSelection({
      selectedCountryCode,
      countries,
      currentUser,
      pathToken,
    })

    if (selectedSeriesId && activeSeries?.prefix && activeSeries?.number === selectedSeriesId) {
      nextToken = activeSeries.token
    }

    const myCupQuery = selectedCountryCode === MY_CUP_VALUE && myCupTeamId ? `?${myCupTeamId}` : ''
    const nextPath = `${nextToken ? `/${encodeURIComponent(nextToken)}` : '/'}${myCupQuery}`

    if (window.location.pathname !== nextPath) {
      window.history.replaceState({}, document.title, nextPath)
    }

    if (pathToken !== nextToken) {
      setPathToken(nextToken)
    }
  }, [selectedCountryCode, countries, currentUser, myCupTeamId, pathToken, selectedSeriesId, activeSeries])

  useEffect(() => {
    const checkSession = async () => {
      if (!oauthToken) {
        setIsAuthenticated(false)
        setCurrentUser(null)
        setCanSync(false)
        setFavoriteTeamIds([])
        setSessionReady(true)
        return
      }

      setSessionReady(false)
      setStatus('Checking session...')

      try {
        const session = await getSession(oauthToken)
        setIsAuthenticated(Boolean(session.authenticated))
        setCurrentUser(session.user || null)
        setCanSync(Boolean(session.canSync))

        if (session.authenticated) {
          setStatus('')
        } else {
          setStatus('Login in progress. Please open the login link again.')
        }
      } catch (error) {
        setIsAuthenticated(false)
        setCurrentUser(null)
        setCanSync(false)
        setFavoriteTeamIds([])
        setStatus('Session verification failed.')
      } finally {
        setSessionReady(true)
      }
    }

    checkSession()
  }, [oauthToken])

  useEffect(() => {
    if (!sessionReady) {
      return
    }

    fetchTeams(selectedCountryCode)
  }, [sessionReady, selectedCountryCode, appliedFilters, fetchTeams])

  useEffect(() => {
    if (!isFilterCountrySelection && !isSavedFilterSelection && isFilterOpen) {
      setIsFilterOpen(false)
    }
  }, [isFilterCountrySelection, isSavedFilterSelection, isFilterOpen])

  useEffect(() => {
    if (isFilterCountrySelection && !isFilterOpen) {
      setIsFilterOpen(true)
    }
  }, [isFilterCountrySelection, isFilterOpen])

  useEffect(() => {
    if (!canSync && isAdminOpen) {
      setIsAdminOpen(false)
    }
  }, [canSync, isAdminOpen])

  useEffect(() => {
    if (selectedCountryCode !== MAX_YOUTH_VALUE) {
      return
    }

    setTableMode(currentMode => (
      currentMode === TABLE_MODE_PLAYERS ? currentMode : TABLE_MODE_PLAYERS
    ))

    setSortConfig(currentSort => {
      if (currentSort.key === 'youthTopSalary' && currentSort.direction === 'desc') {
        return currentSort
      }

      return { key: 'youthTopSalary', direction: 'desc' }
    })
  }, [selectedCountryCode])

  const handleLogin = async () => {
    try {
      const login = await startLogin()
      localStorage.setItem('oauth_token', login.oauthToken)
      setOauthToken(login.oauthToken)
      window.location.href = login.authorizeUrl
    } catch (error) {
      setStatus('Failed to initiate login.')
    }
  }

  const handleLogout = () => {
    localStorage.removeItem('oauth_token')
    setOauthToken(null)
    setIsAuthenticated(false)
    setCurrentUser(null)
    setCanSync(false)
    setFavoriteTeamIds([])
    setStatus('You have been logged out.')
  }

  const handleSync = async () => {
    if (!oauthToken) {
      setStatus('By login in you can add favourites and track your league teams. Without login you see only the top50 teams.')
      return
    }

    if (!canSync) {
      setStatus('Sync is only allowed for administrators.')
      return
    }

    setSyncing(true)

    try {
      const result = await runSync(oauthToken)
      setStatus(`Sync completed. Updated ${result.result.processed} teams.`)
      await refreshLastSuccessfulTeamUpdate()
      await fetchTeams(selectedCountryCode)
      await fetchCountries()
    } catch (error) {
      setStatus('Sync failed.')
    } finally {
      setSyncing(false)
    }
  }

  const handleFlagSync = async () => {
    if (!canSync) {
      setStatus('Sync is only allowed for administrators.')
      return
    }

    setFlagSyncing(true)

    try {
      const result = await runFlagSync(oauthToken)
      setStatus(
        `Flag sync completed. Updated ${result.result.updated} / ${result.result.teamCount} teams.`
      )
      await fetchTeams(selectedCountryCode)
      await fetchCountries()
    } catch (error) {
      setStatus('Flag sync failed.')
    } finally {
      setFlagSyncing(false)
    }
  }

  const handlePlayerSync = async () => {
    if (!oauthToken) {
      setStatus('By login in you can add favourites and track your league teams. Without login you see only the top50 teams.')
      return
    }

    if (!canSync) {
      setStatus('Player sync is only allowed for administrators.')
      return
    }

    setPlayerSyncing(true)

    try {
      const result = await runPlayerSync(oauthToken)
      setStatus(
        `Player sync completed. Updated ${result.result.updated} / ${result.result.teamCount} teams.`
      )
      await fetchTeams(selectedCountryCode)
    } catch (error) {
      setStatus('Player sync failed.')
    } finally {
      setPlayerSyncing(false)
    }
  }

  const handleOldPlayerDetailsSync = async () => {
    if (!oauthToken) {
      setStatus('By login in you can add favourites and track your league teams. Without login you see only the top50 teams.')
      return
    }

    if (!canSync) {
      setStatus('Old player details sync is only allowed for administrators.')
      return
    }

    setOldPlayerDetailsSyncing(true)

    try {
      const result = await runOldPlayerDetailsSync(oauthToken)
      setStatus(
        `Old player details sync completed. Updated ${result.result.updated} teams, missing in backup ${result.result.missingInBackup}.`
      )
      await fetchTeams(selectedCountryCode)
    } catch (error) {
      setStatus('Old player details sync failed.')
    } finally {
      setOldPlayerDetailsSyncing(false)
    }
  }

  const handleMatchSync = async () => {
    if (!oauthToken) {
      setStatus('By login in you can add favourites and track your league teams. Without login you see only the top50 teams.')
      return
    }

    if (!canSync) {
      setStatus('Match sync is only allowed for administrators.')
      return
    }

    setMatchSyncing(true)

    try {
      const result = await runMatchSync(oauthToken)
      setStatus(
        `Match sync completed. Updated ${result.result.updatedTeams} teams, added ${result.result.addedMatches} new matches.`
      )
      await fetchTeams(selectedCountryCode)
    } catch (error) {
      setStatus('Match sync failed.')
    } finally {
      setMatchSyncing(false)
    }
  }

  const handleBotCleanupSync = async () => {
    if (!oauthToken) {
      setStatus('By login in you can add favourites and track your league teams. Without login you see only the top50 teams.')
      return
    }

    if (!canSync) {
      setStatus('Bot cleanup is only allowed for administrators.')
      return
    }

    setBotCleanupSyncing(true)

    try {
      const result = await runBotCleanupSync(oauthToken)
      setStatus(
        `Bot cleanup completed. Removed ${result.result.removed} teams, skipped ${result.result.skipped}, failed ${result.result.failed}.`
      )
      await fetchTeams(selectedCountryCode)
      await fetchCountries()
    } catch (error) {
      setStatus('Bot cleanup failed.')
    } finally {
      setBotCleanupSyncing(false)
    }
  }

  const handleCountrySync = async () => {
    if (!oauthToken) {
      setStatus('By login in you can add favourites and track your league teams. Without login you see only the top50 teams.')
      return
    }

    if (!canSync) {
      setStatus('Country sync is only allowed for administrators.')
      return
    }

    setCountrySyncing(true)

    try {
      await runCountrySync(oauthToken)
      setStatus('Country sync completed.')
      await fetchCountries()
      await fetchTeams(selectedCountryCode)
    } catch (error) {
      setStatus('Country sync failed.')
    } finally {
      setCountrySyncing(false)
    }
  }

  const toggleFavoriteSelection = async teamId => {
    if (!oauthToken || !isAuthenticated) {
      setStatus('Kirjaudu sisaan kayttaaksesi suosikkeja.')
      return
    }

    try {
      const result = await toggleFavoriteTeam({
        oauthToken,
        teamId,
      })

      const ids = Array.isArray(result?.favoriteTeamIds)
        ? result.favoriteTeamIds.map(value => Number.parseInt(value, 10)).filter(Number.isInteger)
        : []

      setFavoriteTeamIds(ids)

      if (selectedCountryCode === FAVORITE_VALUE) {
        await fetchTeams(selectedCountryCode)
      }
    } catch (error) {
      setStatus('Suosikin paivitys epaonnistui.')
    }
  }

  const isOwnRow = team => {
    const loginName = `${currentUser?.LoginName || currentUser?.loginName || ''}`.trim().toLowerCase()
    const userFlag = `${team?.UserFlag || ''}`.trim().toLowerCase()

    const isLoginNameMatch = Boolean(loginName) && userFlag === loginName
    const isTeamIdMatch = Number(team?.TeamID) === Number(currentUser?.teamId)

    return isLoginNameMatch || isTeamIdMatch
  }

  const handleAddLive = () => {
    if (!teamRows.length) {
      setStatus('No teams available for Add Live.')
      return
    }

    const ownTeam = teamRows.find(team => isOwnRow(team))
    const ownTeamId = Number.parseInt(ownTeam?.TeamID, 10)
    const teamsWithoutOwn = teamRows.filter(team => Number.parseInt(team?.TeamID, 10) !== ownTeamId)
    const ownMatch = selectAddLiveMatch(ownTeam?.UpcomingMatches)
    const candidates = teamsWithoutOwn
      .map(team => ({
        team,
        match: selectAddLiveMatch(team?.UpcomingMatches),
      }))
      .filter(candidate => Number.isInteger(Number.parseInt(candidate?.match?.MatchID, 10)))

    const topCupCandidates = candidates
      .filter(candidate => {
        const matchType = Number.parseInt(candidate?.match?.MatchTypeID ?? candidate?.match?.MatchType, 10)
        const cupLevel = Number.parseInt(candidate?.match?.CupLevel, 10)

        return matchType === 3 && cupLevel === 1
      })
      .map(candidate => candidate.team)

    const fallbackCandidates = candidates
      .filter(candidate => {
        const matchType = Number.parseInt(candidate?.match?.MatchTypeID ?? candidate?.match?.MatchType, 10)
        const cupLevel = Number.parseInt(candidate?.match?.CupLevel, 10)

        return !(matchType === 3 && cupLevel === 1)
      })
      .map(candidate => candidate.team)

    const prioritizedNonOwnTeams = [
      ...sampleTeams({ items: topCupCandidates, size: 25 }),
      ...sampleTeams({
        items: fallbackCandidates,
        size: Math.max(0, 25 - topCupCandidates.length),
      }),
    ].slice(0, 25)

    const sampledTeams = [...prioritizedNonOwnTeams]

    if (ownTeam && ownMatch && !sampledTeams.some(team => Number.parseInt(team?.TeamID, 10) === ownTeamId)) {
      sampledTeams.unshift(ownTeam)
    }

    const matchIds = Array.from(new Set(
      sampledTeams
        .map(team => selectAddLiveMatch(team?.UpcomingMatches))
        .map(match => Number.parseInt(match?.MatchID, 10))
        .filter(matchId => Number.isInteger(matchId) && matchId > 0)
    ))

    if (!matchIds.length) {
      setStatus('No upcoming matches found for Add Live.')
      return
    }

    const livePath = `/Club/Matches/Live.aspx?matchID=${matchIds.join(',')}&actionType=addMatch&SourceSystem=Hattrick`
    const liveUrl = `https://hattrick.org/goto.ashx?path=${encodeURIComponent(livePath)}`
    window.open(liveUrl, '_blank', 'noopener,noreferrer')

    setStatus(`Add Live opened with ${matchIds.length} match IDs.`)
  }

  const isAddLiveSelection =
    selectedCountryCode === DISCORD_NO_VALUE || selectedCountryCode === FAVORITE_VALUE || isSavedFilterSelection

  const updateFilterDraft = (key, value) => {
    setFilterDraft(current => ({
      ...current,
      [key]: value,
    }))
  }

  const applyFilters = () => {
    setAppliedFilters(filterDraft)
    if (selectedCountryCode !== '' && !isSavedFilterSelection && !isFilterCountrySelection) {
      setSelectedCountryCode('')
    }
  }

  const clearFilters = () => {
    setFilterDraft(FILTER_DEFAULTS)
    setAppliedFilters(FILTER_DEFAULTS)
  }

  const updateSavedFilter = async () => {
    if (!selectedSavedFilter) {
      setStatus('Open a saved country before updating it.')
      return
    }

    const name = `${filterNameDraft || ''}`.trim()

    if (!name) {
      setStatus('Filter name is required.')
      return
    }

    if (hasSavedFilterNameConflict(name, selectedSavedFilter.id)) {
      setStatus('A saved filter with that name already exists.')
      return
    }

    const normalizedFilters = normalizeFilterValues(filterDraft)

    const updatedFilter = {
      ...selectedSavedFilter,
      name,
      filters: normalizedFilters,
    }

    const nextSavedFilters = savedFilters.map(filter => (
      filter.id === selectedSavedFilter.id ? updatedFilter : filter
    ))

    try {
      const result = await saveUserSavedFilters({
        oauthToken,
        savedFilters: nextSavedFilters,
      })

      setSavedFilters(normalizeSavedFilters(result?.savedFilters))
      setAppliedFilters(normalizedFilters)
      setFilterDraft(normalizedFilters)
      setSelectedCountryCode(`${SAVED_FILTER_VALUE_PREFIX}${selectedSavedFilter.id}`)
      setPathToken(`FILTER-${selectedSavedFilter.id}`)
      loadedSavedFilterIdRef.current = selectedSavedFilter.id
      setStatus(`Saved filter "${name}" updated.`)
    } catch (error) {
      setStatus('Saving the updated filter failed.')
    }
  }

  const saveNewFilter = async () => {
    const name = `${filterNameDraft || ''}`.trim()

    if (!name) {
      setStatus('Filter name is required.')
      return
    }

    if (hasSavedFilterNameConflict(name)) {
      setStatus('A saved filter with that name already exists.')
      return
    }

    const normalizedFilters = normalizeFilterValues(filterDraft)
    const newSavedFilter = {
      id: createSavedFilterId(),
      name,
      filters: normalizedFilters,
    }

    const nextSavedFilters = [newSavedFilter, ...savedFilters]

    try {
      const result = await saveUserSavedFilters({
        oauthToken,
        savedFilters: nextSavedFilters,
      })

      setSavedFilters(normalizeSavedFilters(result?.savedFilters))
      setAppliedFilters(normalizedFilters)
      setSelectedCountryCode(`${SAVED_FILTER_VALUE_PREFIX}${newSavedFilter.id}`)
      setPathToken(`FILTER-${newSavedFilter.id}`)
      loadedSavedFilterIdRef.current = newSavedFilter.id
      setStatus(`Saved filter "${name}" added to country list.`)
    } catch (error) {
      setStatus('Saving the new filter failed.')
    }
  }

  const deleteSavedFilter = async filterId => {
    const nextFilterId = `${filterId || ''}`

    if (!nextFilterId) {
      return
    }

    const nextSavedFilters = savedFilters.filter(filter => filter.id !== nextFilterId)

    try {
      const result = await saveUserSavedFilters({
        oauthToken,
        savedFilters: nextSavedFilters,
      })

      setSavedFilters(normalizeSavedFilters(result?.savedFilters))
    } catch (error) {
      setStatus('Deleting the saved filter failed.')
      return
    }

    if (`${selectedCountryCode || ''}` === `${SAVED_FILTER_VALUE_PREFIX}${nextFilterId}`) {
      setSelectedCountryCode('')
      setFilterDraft(FILTER_DEFAULTS)
      setAppliedFilters(FILTER_DEFAULTS)
      setFilterNameDraft('')
      setPathToken('')
      loadedSavedFilterIdRef.current = ''
      setStatus('Saved filter removed from country list.')
    }
  }

  const filteredCountries = (searchValue => {
    const normalized = `${searchValue || ''}`.trim().toLowerCase()

    if (!normalized) {
      return countriesForSelect
    }

    return countriesForSelect.filter(country => {
      const name = `${normalizeCountryName(country.CountryName) || ''}`.trim().toLowerCase()
      const code = `${country.CountryCode || ''}`.trim().toLowerCase()
      const id = `${country.CountryID || ''}`.trim().toLowerCase()
      return name.includes(normalized) || code.includes(normalized) || id.includes(normalized)
    })
  })

  const renderMultiSelectDropdown = ({
    label,
    fieldKey,
    values,
    options,
    resolveLabel,
    searchValue,
    onSearchChange,
    searchPlaceholder,
    onClear,
  }) => {
    const visibleOptions = searchValue
      ? options.filter(option => {
          const normalizedSearch = `${searchValue || ''}`.trim().toLowerCase()
          const normalizedLabel = `${option.label || ''}`.trim().toLowerCase()
          const normalizedValue = `${option.value || ''}`.trim().toLowerCase()

          return normalizedLabel.includes(normalizedSearch) || normalizedValue.includes(normalizedSearch)
        })
      : options

    return (
      <div className="filterField">
        <span>{label}</span>
        <Dropdown className="countryDropdown" autoClose="outside">
          <Dropdown.Toggle className="countryDropdownToggle" variant="secondary">
            {getMultiSelectSummary(values, resolveLabel)}
          </Dropdown.Toggle>
          <Dropdown.Menu className="countryDropdownMenu">
            {onSearchChange ? (
              <div className="countryDropdownSearch">
                <input
                  type="text"
                  placeholder={searchPlaceholder || 'Search'}
                  value={searchValue || ''}
                  onChange={event => onSearchChange(event.target.value)}
                />
              </div>
            ) : null}
            <Dropdown.Item
              onClick={onClear}
              className={!values.length ? 'active' : undefined}
            >
              Any
            </Dropdown.Item>
            {visibleOptions.map(option => {
              const isSelected = values.includes(option.value)

              return (
                <Dropdown.Item
                  key={`${fieldKey}-${option.value}`}
                  onClick={() => toggleMultiSelectValue(fieldKey, option.value)}
                  className={isSelected ? 'active' : undefined}
                >
                  <span>{option.label}</span>
                  {isSelected ? <span className="multiSelectActiveMark">Active</span> : null}
                </Dropdown.Item>
              )
            })}
          </Dropdown.Menu>
        </Dropdown>
      </div>
    )
  }

  return (
    <main className="page">
      {isUpdateInProgress ? (
        <div className="teamUpdateBanner" role="status" aria-live="assertive">
          <strong>
            {updateProgressLabel}
            {activeUpdate?.total ? ` (${activeUpdate.completed} / ${activeUpdate.total})` : ''}
          </strong>
          <div
            className="teamUpdateProgress"
            role="progressbar"
              aria-label="Updating teams"
              aria-valuetext="Update in progress"
              aria-valuemin="0"
              aria-valuemax={activeUpdate?.total || undefined}
              aria-valuenow={activeUpdate?.total ? activeUpdate.completed : undefined}
            >
            <div
              className={`teamUpdateProgressBar${updateProgressPercent === null ? ' isIndeterminate' : ''}`}
              style={updateProgressPercent === null ? undefined : { width: `${updateProgressPercent}%` }}
            />
          </div>
        </div>
      ) : null}
      <section className="hero">
        <a
          href="https://hattrick.org/goto.ashx?path=/Community/CHPP/ChppProgramDetails.aspx?ApplicationId=5331"
          target="_blank"
          rel="noreferrer"
          className="chppLink"
          aria-label="Open CHPP program details"
        >
          <img src="/chpp.svg" alt="CHPP" width="70" height="70" />
        </a>
        <div className="heroHeader">
          <h1>Femmeleague</h1>
        </div>
        <p>{status}</p>
        <div className="lastUpdatedBlock">
          <div className="lastUpdatedRow">
            {firstTeamLastUpdatedLabel ? (
              <p className="lastUpdatedNotice">
                Teams last updated: <strong>{firstTeamLastUpdatedLabel}</strong>
              </p>
            ) : null}
            <button
              type="button"
              className="updateScheduleToggle"
              onClick={() => setIsUpdateScheduleOpen(open => !open)}
              aria-expanded={isUpdateScheduleOpen}
              aria-controls="update-schedule"
              aria-label="Show automatic update schedule"
              title="Automatic update schedule"
            >
              i
            </button>
          </div>
          {isUpdateScheduleOpen ? (
            <section id="update-schedule" className="updateSchedule" aria-label="Automatic update schedule">
              <h2>Automatic updates ({userTimeZone})</h2>
              <div className="updateScheduleRows">
                {automaticUpdateSchedule.map(update => (
                  <div className="updateScheduleRow" key={update.label}>
                    <span>{update.label}</span>
                    <strong>{update.schedule || 'Time unavailable'}</strong>
                  </div>
                ))}
              </div>
            </section>
          ) : null}
        </div>
        <div className="actions">
          <ToggleButtonGroup
            className="countryScopeSwitch"
            type="radio"
            name="country-scope"
            value={countryScope}
            onChange={value => setCountryScope(value)}
            aria-label="Country scope"
          >
            <ToggleButton id="country-scope-user" value={COUNTRY_SCOPE_USER} variant="outline-light" size="sm">
              User
            </ToggleButton>
            <ToggleButton id="country-scope-team" value={COUNTRY_SCOPE_TEAM} variant="outline-light" size="sm">
              Team
            </ToggleButton>
          </ToggleButtonGroup>
          <select
            className={favoriteSelectClassName}
            value={selectedCountryCode}
            onChange={event => handleCountrySelectionChange(event.target.value)}
            disabled={isCountriesLoading}
          >
            <option value="">All countries</option>
            {isAuthenticated ? (
              <option
                value={FAVORITE_VALUE}
                className={specialThemeClasses?.option || undefined}
              >
                {favoriteOptionLabel}
              </option>
            ) : null}
            {isAuthenticated || selectedCountryCode === MY_CUP_VALUE ? <option value={MY_CUP_VALUE}>My Cup</option> : null}
            <option value={AVERAGE_TEAM_VALUE}>Median team</option>
            {countryExtraOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
            <option value={FILTER_COUNTRY_VALUE}>Filter</option>
            {savedFilterOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
            {countriesForSelect.map(country => (
              <option key={country.CountryID} value={`${country.CountryID || ''}`}>
                {(normalizeCountryName(country.CountryName) || country.CountryCode || country.CountryID) +
                  ` (${country.teamCount || 0})`}
              </option>
            ))}
          </select>
          <ToggleButtonGroup
            className="tableModeSwitch"
            type="radio"
            name="table-mode"
            value={tableMode}
            onChange={value => setTableMode(value)}
            aria-label="Table mode"
          >
            <ToggleButton id="table-mode-team" value={TABLE_MODE_TEAM} variant="outline-light" size="sm">
              Team
            </ToggleButton>
            <ToggleButton id="table-mode-players" value={TABLE_MODE_PLAYERS} variant="outline-light" size="sm">
              Players
            </ToggleButton>
          </ToggleButtonGroup>
          <ToggleButtonGroup
            className="statsScopeSwitch"
            type="radio"
            name="player-stats-scope"
            value={playerStatsScope}
            onChange={value => setPlayerStatsScope(value)}
            aria-label="Player stats scope"
          >
            <ToggleButton
              id="stats-scope-all"
              value={PLAYER_STATS_SCOPE_ALL}
              variant="outline-light"
              size="sm"
              disabled={tableMode === TABLE_MODE_TEAM}
            >
              All
            </ToggleButton>
            <ToggleButton
              id="stats-scope-top11"
              value={PLAYER_STATS_SCOPE_TOP11}
              variant="outline-light"
              size="sm"
              disabled={tableMode === TABLE_MODE_TEAM}
            >
              Top11
            </ToggleButton>
          </ToggleButtonGroup>
          {isAuthenticated && isAddLiveSelection ? (
            <Button
              type="button"
              variant="warning"
              onClick={handleAddLive}
              disabled={isTeamsLoading || !teamRows.length}
            >
              Add Live
            </Button>
          ) : null}
          <div className="actionsSpacer" />
          <div className="authPanel">
            <div className="authState">
              {isAuthenticated && (currentUser?.LoginName || currentUser?.loginName) ? (
                <span>
                  logged in as{' '}
                  <span className={specialThemeClasses?.loginName || undefined}>
                    {currentUser.LoginName || currentUser.loginName}
                  </span>
                </span>
              ) : null}
            </div>
            <Button
              type="button"
              className="authButton"
              variant={isAuthenticated ? 'danger' : 'success'}
              onClick={isAuthenticated ? handleLogout : handleLogin}
            >
              {isAuthenticated ? 'Log Out' : 'Log In'}
            </Button>
          </div>
        </div>
          <div className="filterToolbar">
            {canSync ? (
              <button
                type="button"
                className={`filterToggle ${isAdminOpen ? 'active' : ''}`}
                onClick={() => setIsAdminOpen(open => !open)}
              >
                Admin
              </button>
            ) : null}
          </div>
        {canSync && isAdminOpen ? (
          <div className="filterPanel adminPanel">
            <div className="adminActions">
              <button type="button" onClick={handleSync} disabled={!isAuthenticated || syncing}>
                {syncing ? 'Updating...' : 'Update teams'}
              </button>
              <button
                type="button"
                onClick={handleCountrySync}
                disabled={
                  countrySyncing ||
                  syncing ||
                  flagSyncing ||
                  playerSyncing ||
                  matchSyncing ||
                  botCleanupSyncing
                }
              >
                {countrySyncing ? 'Syncing countries...' : 'Sync countries'}
              </button>
              <button
                type="button"
                onClick={handleFlagSync}
                disabled={flagSyncing || syncing}
              >
                {flagSyncing ? 'Syncing flags...' : 'Sync flags'}
              </button>
              <button
                type="button"
                onClick={handlePlayerSync}
                disabled={
                  playerSyncing ||
                  syncing ||
                  flagSyncing ||
                  oldPlayerDetailsSyncing ||
                  matchSyncing ||
                  botCleanupSyncing
                }
              >
                {playerSyncing ? 'Syncing players...' : 'Sync players'}
              </button>
              <button
                type="button"
                onClick={handleOldPlayerDetailsSync}
                disabled={
                  oldPlayerDetailsSyncing ||
                  syncing ||
                  flagSyncing ||
                  playerSyncing ||
                  matchSyncing ||
                  botCleanupSyncing
                }
              >
                {oldPlayerDetailsSyncing ? 'Syncing old details...' : 'Sync old player details'}
              </button>
              <button
                type="button"
                onClick={handleMatchSync}
                disabled={
                  matchSyncing ||
                  syncing ||
                  flagSyncing ||
                  playerSyncing ||
                  oldPlayerDetailsSyncing ||
                  botCleanupSyncing
                }
              >
                {matchSyncing ? 'Syncing matches...' : 'Sync matches'}
              </button>
              <button
                type="button"
                onClick={handleBotCleanupSync}
                disabled={
                  botCleanupSyncing ||
                  syncing ||
                  flagSyncing ||
                  playerSyncing ||
                  oldPlayerDetailsSyncing ||
                  matchSyncing
                }
              >
                {botCleanupSyncing ? 'Cleaning bots...' : 'Bot cleanup'}
              </button>
            </div>
          </div>
        ) : null}
        {isAuthenticated && (isFilterCountrySelection || isSavedFilterSelection) ? (
          <div className="filterPanel">
            <div className="filterSaveRow">
              <label className="filterField filterNameField">
                <input
                  type="text"
                  placeholder="e.g. My league targets"
                  value={filterNameDraft}
                  onChange={event => setFilterNameDraft(event.target.value)}
                />
              </label>
              <div className="filterSaveActions">
                {selectedSavedFilter ? (
                  <button
                    type="button"
                    className="updateFilterAction"
                    onClick={updateSavedFilter}
                  >
                    Update filter
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={saveNewFilter}
                  disabled={!`${filterNameDraft || ''}`.trim()}
                >
                  Save new
                </button>
                {selectedSavedFilter ? (
                  <button
                    type="button"
                    className="dangerAction"
                    onClick={() => selectedSavedFilter && deleteSavedFilter(selectedSavedFilter.id)}
                  >
                    Delete
                  </button>
                ) : null}
              </div>
            </div>
            <div className="filterGrid">
              <label className="filterField">
                <span>Result limit</span>
                <input
                  type="number"
                  min="1"
                  max="15000"
                  value={filterDraft.filterLimit}
                  onChange={event => {
                    const rawValue = event.target.value
                    if (rawValue === '') {
                      updateFilterDraft('filterLimit', '')
                      return
                    }

                    const parsed = Number.parseInt(rawValue, 10)
                    if (Number.isNaN(parsed)) {
                      updateFilterDraft('filterLimit', '')
                      return
                    }

                    const nextValue = Math.min(Math.max(parsed, 1), 15000)
                    updateFilterDraft('filterLimit', `${nextValue}`)
                  }}
                />
              </label>
              
              {renderMultiSelectDropdown({
                label: 'User country',
                fieldKey: 'userCountryId',
                values: getMultiSelectValues('userCountryId'),
                options: filteredCountries(filterDraft.userCountrySearch).map(country => ({
                  value: `${country.CountryID || ''}`,
                  label: getCountryLabel(country),
                })),
                resolveLabel: value => getCountryLabelById(value),
                searchValue: filterDraft.userCountrySearch,
                onSearchChange: value => updateFilterDraft('userCountrySearch', value),
                searchPlaceholder: 'Search country',
                onClear: () => clearMultiSelectValues('userCountryId'),
              })}
              {renderMultiSelectDropdown({
                label: 'Team country',
                fieldKey: 'teamCountryId',
                values: getMultiSelectValues('teamCountryId'),
                options: filteredCountries(filterDraft.teamCountrySearch).map(country => ({
                  value: `${country.CountryID || ''}`,
                  label: getCountryLabel(country),
                })),
                resolveLabel: value => getCountryLabelById(value),
                searchValue: filterDraft.teamCountrySearch,
                onSearchChange: value => updateFilterDraft('teamCountrySearch', value),
                searchPlaceholder: 'Search country',
                onClear: () => clearMultiSelectValues('teamCountryId'),
              })}
              {renderMultiSelectDropdown({
                label: 'Cup',
                fieldKey: 'cup',
                values: getMultiSelectValues('cup'),
                options: CUP_FILTER_OPTIONS.filter(option => option.value !== '').map(option => ({
                  value: option.value,
                  label: option.label,
                })),
                resolveLabel: value => CUP_FILTER_OPTIONS.find(option => option.value === value)?.label || value,
                onClear: () => clearMultiSelectValues('cup'),
              })}
              {renderMultiSelectDropdown({
                label: 'Coach type',
                fieldKey: 'coachType',
                values: getMultiSelectValues('coachType'),
                options: [
                  { value: 'D', label: 'Defensive' },
                  { value: 'O', label: 'Offensive' },
                  { value: 'N', label: 'Neutral' },
                ],
                resolveLabel: value => (value === 'D' ? 'Defensive' : value === 'O' ? 'Offensive' : value === 'N' ? 'Neutral' : value),
                onClear: () => clearMultiSelectValues('coachType'),
              })}
              <label className="filterField">
                <span>Youth</span>
                <select
                  value={filterDraft.youth}
                  onChange={event => updateFilterDraft('youth', event.target.value)}
                >
                  <option value="">Any</option>
                  <option value="network">Network</option>
                  <option value="team">Team</option>
                </select>
              </label>
              {renderMultiSelectDropdown({
                label: 'Series',
                fieldKey: 'seriesLevel',
                values: getMultiSelectValues('seriesLevel'),
                options: SERIES_FILTER_OPTIONS.filter(option => option.value !== '').map(option => ({
                  value: option.value,
                  label: option.label,
                })),
                resolveLabel: value => SERIES_FILTER_OPTIONS.find(option => option.value === value)?.label || value,
                onClear: () => clearMultiSelectValues('seriesLevel'),
              })}
              <label className="filterField">
                <span>Coach skill</span>
                <div className="filterRange">
                  <input
                    type="number"
                    min="0"
                    placeholder="Min"
                    value={filterDraft.coachSkillMin}
                    onChange={event => updateFilterDraft('coachSkillMin', event.target.value)}
                  />
                  <input
                    type="number"
                    min="0"
                    placeholder="Max"
                    value={filterDraft.coachSkillMax}
                    onChange={event => updateFilterDraft('coachSkillMax', event.target.value)}
                  />
                </div>
              </label>
              <label className="filterField">
                <span>Coach leadership</span>
                <div className="filterRange">
                  <input
                    type="number"
                    min="0"
                    placeholder="Min"
                    value={filterDraft.coachLeadershipMin}
                    onChange={event => updateFilterDraft('coachLeadershipMin', event.target.value)}
                  />
                  <input
                    type="number"
                    min="0"
                    placeholder="Max"
                    value={filterDraft.coachLeadershipMax}
                    onChange={event => updateFilterDraft('coachLeadershipMax', event.target.value)}
                  />
                </div>
              </label>
              <label className="filterField">
                <span>Fans</span>
                <div className="filterRange">
                  <input
                    type="number"
                    min="0"
                    max="4000"
                    placeholder="Min"
                    value={filterDraft.fansMin}
                    onChange={event => updateFilterDraft('fansMin', event.target.value)}
                  />
                  <input
                    type="number"
                    min="0"
                    max="4000"
                    placeholder="Max"
                    value={filterDraft.fansMax}
                    onChange={event => updateFilterDraft('fansMax', event.target.value)}
                  />
                </div>
              </label>
              <label className="filterField">
                <span>Arena</span>
                <div className="filterRange">
                  <input
                    type="number"
                    min="0"
                    max="100000"
                    placeholder="Min"
                    value={filterDraft.arenaMin}
                    onChange={event => updateFilterDraft('arenaMin', event.target.value)}
                  />
                  <input
                    type="number"
                    min="0"
                    max="100000"
                    placeholder="Max"
                    value={filterDraft.arenaMax}
                    onChange={event => updateFilterDraft('arenaMax', event.target.value)}
                  />
                </div>
              </label>
              <label className="filterField">
                <span>Hatstats</span>
                <div className="filterRange">
                  <input
                    type="number"
                    min="0"
                    max="700"
                    placeholder="Min"
                    value={filterDraft.hatstatsMin}
                    onChange={event => updateFilterDraft('hatstatsMin', event.target.value)}
                  />
                  <input
                    type="number"
                    min="0"
                    max="700"
                    placeholder="Max"
                    value={filterDraft.hatstatsMax}
                    onChange={event => updateFilterDraft('hatstatsMax', event.target.value)}
                  />
                </div>
              </label>
              <label className="filterField">
                <span>Pts</span>
                <div className="filterRange">
                  <input
                    type="number"
                    min="0"
                    max="42"
                    placeholder="Min"
                    value={filterDraft.ptsMin}
                    onChange={event => updateFilterDraft('ptsMin', event.target.value)}
                  />
                  <input
                    type="number"
                    min="0"
                    max="42"
                    placeholder="Max"
                    value={filterDraft.ptsMax}
                    onChange={event => updateFilterDraft('ptsMax', event.target.value)}
                  />
                </div>
              </label>
              <label className="filterField">
                <span>Pos</span>
                <div className="filterRange">
                  <input
                    type="number"
                    min="1"
                    max="8"
                    placeholder="Min"
                    value={filterDraft.posMin}
                    onChange={event => updateFilterDraft('posMin', event.target.value)}
                  />
                  <input
                    type="number"
                    min="1"
                    max="8"
                    placeholder="Max"
                    value={filterDraft.posMax}
                    onChange={event => updateFilterDraft('posMax', event.target.value)}
                  />
                </div>
              </label>
              {isAuthenticated && Number.parseInt(currentUser?.countryId, 10) === 11 ? (
                <label className="filterField discord">
                  <span>Discord</span>
                  <div className="discordCheckbox">
                    <input
                      type="checkbox"
                      checked={filterDraft.discord === 'true'}
                      onChange={event => updateFilterDraft('discord', event.target.checked ? 'true' : '')}
                      aria-label="Discord only"
                    />
                    <span className="discordBox" aria-hidden="true" />
                  </div>
                </label>
              ) : null}
            </div>
            {selectedFilterChips.length ? (
              <div className="selectedFilterList" aria-label="Selected filters">
                {selectedFilterChips.map(chip => (
                  <button
                    key={chip.key}
                    type="button"
                    className="selectedFilterChip"
                    onClick={chip.onRemove}
                    title="Remove selection"
                  >
                    <span>{chip.label}</span>
                    <span className="multiSelectActiveMark">Active</span>
                    <span className="selectedFilterRemove">×</span>
                  </button>
                ))}
              </div>
            ) : null}
            <div className="filterActions">
              <button
                type="button"
                onClick={applyFilters}
                disabled={!filtersDirty}
              >
                Apply filters
              </button>
              <button
                type="button"
                onClick={clearFilters}
                disabled={!hasDraftFilters && !hasActiveFilters}
              >
                Clear
              </button>
            </div>
          </div>
        ) : null}
      </section>

      {isBotSeriesSelection ? (
        <BotSeriesTable isLoading={isBotSeriesLoading} rows={botSeriesRows} />
      ) : (
        <>
          {selectedCountryCode !== MY_CUP_VALUE ? (
            <TeamsTable
          isTeamsLoading={isTeamsLoading}
          seriesControls={selectedSeriesId ? (
            <div className="seriesNavWrap" aria-label="Series navigation">
              <button
                type="button"
                className="seriesNavButton"
                onClick={goToPreviousSeries}
                disabled={!selectedSeriesId || selectedSeriesId <= 1}
                aria-label="Previous series"
                title="Previous series"
              >
                {'<'}
              </button>
              <input
                type="text"
                className="seriesJumpInput"
                value={seriesJumpInput}
                onChange={event => setSeriesJumpInput(event.target.value.toUpperCase())}
                onKeyDown={event => {
                  if (event.key === 'Enter') {
                    jumpToSeries(seriesJumpInput)
                  }
                }}
                placeholder="VII.1443"
                aria-label="Jump to series"
              />
              <button
                type="button"
                className="seriesJumpButton"
                onClick={() => jumpToSeries(seriesJumpInput)}
              >
                Open
              </button>
              <button
                type="button"
                className="seriesNavButton"
                onClick={goToNextSeries}
                disabled={!selectedSeriesId}
                aria-label="Next series"
                title="Next series"
              >
                {'>'}
              </button>
            </div>
          ) : null}
          columns={visibleColumns}
          tableMode={tableMode}
          sortConfig={sortConfig}
          databaseSearchDirections={databaseSearchDirections}
          showDatabaseSearchRow={selectedCountryCode === '' && !hasActiveFilters}
          getColumnClassName={getColumnClassName}
          toggleSort={toggleSort}
          onDatabaseSearch={runDatabaseSearch}
          renderColumnLabel={column => renderColumnLabel(column, {
            sortConfig,
            onCoachSkillSort: sortByCoachSkill,
            onCoachLeadershipSort: sortByCoachLeadership,
          })}
          getSortArrow={getSortArrow}
          teamRows={teamRows}
          isOwnRow={isOwnRow}
          favoriteTeamIds={favoriteTeamIds}
          isAuthenticated={isAuthenticated}
          ownRowSpecialClassName={specialThemeClasses?.ownRow || ''}
          toggleFavoriteSelection={toggleFavoriteSelection}
            />
          ) : null}
          {selectedCountryCode === MY_CUP_VALUE && isTeamsLoading ? (
            <section className="tableWrap">
              <div className="loadingState" role="status" aria-live="polite">
                <Spinner animation="border" variant="light" />
                <span>Loading cup data...</span>
              </div>
            </section>
          ) : null}
          {selectedCountryCode === MY_CUP_VALUE && !isTeamsLoading && cupMatches.length ? (
            <section className="cupBracket" aria-label="My cup bracket">
              <h2>My cup matches</h2>
              <div className="myCupLookup">
                <label htmlFor="my-cup-team-search">Find team/user</label>
                <input
                  id="my-cup-team-search"
                  type="search"
                  value={myCupSearchTerm}
                  onChange={event => setMyCupSearchTerm(event.target.value)}
                  placeholder="First 4 letters"
                  autoComplete="off"
                />
                {isMyCupSearchLoading ? <span className="myCupLookupStatus">Searching...</span> : null}
                {myCupSearchResults.length ? (
                  <div className="myCupLookupResults" role="listbox" aria-label="Matching teams">
                    {myCupSearchResults.map(result => (
                      <button key={result.teamId} type="button" role="option" aria-selected="false" onClick={() => openMyCupTeam(result.teamId)}>
                        <span>{result.teamName}</span>
                        {result.userName ? <small>{result.userName}</small> : null}
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
              <p className="cupRoundFour">{cupTeam?.teamName || 'This team'} plays at home if it advances: {cupTeam?.teamName || 'This team'} – {cupPairs?.next?.teams?.map(team => team.teamName).join(' / ') || '-'}</p>
              <p className="cupRoundFour cupRoundFourPrediction">
                {cupNextMatchPredictions.length
                  ? <>
                      Possible next match: {cupNextMatchPredictions.map((match, index) => (
                        <React.Fragment key={`${match.homeTeamName}:${match.awayTeamName}`}>
                          {index > 0 ? ' or ' : null}
                          {match.homeTeamName} <span className="cupVenueLabel">(Home)</span>
                          {' - '}
                          {match.awayTeamName} <span className="cupVenueLabel">(Away)</span>
                        </React.Fragment>
                      ))}
                    </>
                  : 'Possible next match is not available yet.'}
              </p>
              <h2>Current match</h2>
              <TeamsTable isTeamsLoading={false} columns={cupMatchColumns} tableMode={tableMode} sortConfig={sortConfig} databaseSearchDirections={databaseSearchDirections} showDatabaseSearchRow={false} getColumnClassName={getColumnClassName} toggleSort={toggleSort} onDatabaseSearch={runDatabaseSearch} renderColumnLabel={column => renderColumnLabel(column, { sortConfig, onCoachSkillSort: sortByCoachSkill, onCoachLeadershipSort: sortByCoachLeadership })} getSortArrow={getSortArrow} teamRows={cupCurrentTeamRows} isOwnRow={isOwnRow} favoriteTeamIds={favoriteTeamIds} isAuthenticated={isAuthenticated} ownRowSpecialClassName={specialThemeClasses?.ownRow || ''} toggleFavoriteSelection={toggleFavoriteSelection} />
              <h2>Counter match</h2>
              <TeamsTable isTeamsLoading={false} columns={cupMatchColumns} tableMode={tableMode} sortConfig={sortConfig} databaseSearchDirections={databaseSearchDirections} showDatabaseSearchRow={false} getColumnClassName={getColumnClassName} toggleSort={toggleSort} onDatabaseSearch={runDatabaseSearch} renderColumnLabel={column => renderColumnLabel(column, { sortConfig, onCoachSkillSort: sortByCoachSkill, onCoachLeadershipSort: sortByCoachLeadership })} getSortArrow={getSortArrow} teamRows={cupNextTeamRows} isOwnRow={isOwnRow} favoriteTeamIds={favoriteTeamIds} isAuthenticated={isAuthenticated} ownRowSpecialClassName={specialThemeClasses?.ownRow || ''} toggleFavoriteSelection={toggleFavoriteSelection} />
            </section>
          ) : null}
        </>
      )}
    </main>
  )
}

export default App
